import { BookOpen } from 'lucide-react';

/** Default icon for all subjects — subjects are now dynamic tags */
export const subjectIcons: Record<string, React.ElementType> = {};

/** Default icon when no subject-specific icon is configured */
export const defaultSubjectIcon: React.ElementType = BookOpen;

/**
 * @deprecated Subjects are now stored as human-readable tag names.
 * Components should use `course.subject` directly.
 */
export const subjectLabels: Record<string, string> = {};

const SUBJECT_COLOR_PALETTE = [
  'bg-purple-500/20 text-purple-300',
  'bg-green-500/20 text-green-300',
  'bg-blue-500/20 text-blue-300',
  'bg-yellow-500/20 text-yellow-300',
  'bg-pink-500/20 text-pink-300',
  'bg-cyan-500/20 text-cyan-300',
  'bg-orange-500/20 text-orange-300',
  'bg-red-500/20 text-red-300',
  'bg-indigo-500/20 text-indigo-300',
  'bg-teal-500/20 text-teal-300',
];

/** Deterministic color class for a subject tag — same name always gets the same color. */
export function getSubjectColor(subject: string): string {
  if (!subject) return 'bg-gray-500/20 text-gray-300';
  const key = subject.trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % SUBJECT_COLOR_PALETTE.length;
  return SUBJECT_COLOR_PALETTE[index];
}

const gradeLevelLabels: Record<string, string> = {
  gk: 'GK',
  g1: 'G1',
  g2: 'G2',
  g3: 'G3',
  g4: 'G4',
  g5: 'G5',
  g6: 'G6',
  g7: 'G7',
  g8: 'G8',
  g9: 'G9',
  g10: 'G10',
  g11: 'G11',
  g12: 'G12',
};

const gradeLevelOrder = ['gk', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6', 'g7', 'g8', 'g9', 'g10', 'g11', 'g12'];

export function formatGradeLevels(levels: string[]): string {
  if (levels.length === 0) return '';
  const unique = [...new Set(levels)];
  if (unique.length === 1) return gradeLevelLabels[unique[0]] ?? unique[0];
  // Unknown enum values (indexOf === -1) sort to the end so the comma list
  // stays in enum order for the known values.
  const orderKey = (g: string) => {
    const idx = gradeLevelOrder.indexOf(g);
    return idx === -1 ? Number.POSITIVE_INFINITY : idx;
  };
  const sorted = [...unique].sort((a, b) => orderKey(a) - orderKey(b));
  const indices = sorted.map((g) => gradeLevelOrder.indexOf(g));
  const labels = sorted.map((g) => gradeLevelLabels[g] ?? g);
  const allKnown = indices.every((i) => i >= 0);
  const contiguous =
    allKnown && indices.every((idx, i) => i === 0 || idx - indices[i - 1] === 1);
  if (contiguous) {
    return `${labels[0]}\u2013${labels[labels.length - 1]}`;
  }
  return labels.join(', ');
}

/** Escape `<` to prevent script breakout in JSON-LD embedded in HTML */
export function safeJsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}
